import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { SagaApi } from '../api/saga.api';
import { ISaga, SagaData, SagaStepEnum } from '../models/saga/saga.model';
import { SessionService } from '@core/services/session.service';
import { createState } from '@shared/helpers/create-state.helper';
import moment from 'moment';
import { AppConstants } from '@core/constants';
import { IUseCaseItem } from '../models/use-case/use-case.model';

@Injectable({ providedIn: 'root' })
export class SagaStore {
  private api = inject(SagaApi);
  private destroyRef = inject(DestroyRef);
  private sessionService = inject(SessionService);

  private sagaSubject = new BehaviorSubject<ISaga | null>(null);
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private errorSubject = new BehaviorSubject<string | null>(null);

  saga$ = this.sagaSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();
  error$ = this.errorSubject.asObservable();

  isSagaActive$ = this.saga$.pipe(map((saga) => saga?.status === 'running'));

  isSagaComplete = createState<boolean>(false);

  constructor() {
    const sagaData = this.sessionService.getAuth()?.sagas?.technical_onboarding;
    if (sagaData) {
      this.setSagaData(sagaData);
    }
    this.getLocal();
  }

  setSagaData(saga: ISaga) {
    this.sagaSubject.next(saga);
  }

  clearSagaData() {
    this.clearLocal();
    this.sagaSubject.next(null);
  }

  isCurrentStep(stepName: SagaStepEnum): Observable<boolean> {
    return this.saga$.pipe(map((saga) => saga?.current_step === stepName));
  }

  isTestCreated(): boolean {
    const current = this.sagaSubject.value;
    if (!current) return false;

    const ppdCreated = Boolean(current.saga_data?.ppdCreated);
    const lactateCreated = Boolean(current.saga_data?.lactateCreated);
    return ppdCreated || lactateCreated;
  }

  /** Deprecated - was only used for testing */
  createSaga() {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    const saga: Partial<ISaga> = {
      saga_data: {},
    };

    this.api
      .createSaga(saga)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (newSaga) => {
          this.sagaSubject.next(newSaga as ISaga);
          this.loadingSubject.next(false);
        },
        error: () => {
          this.errorSubject.next('Failed to create saga');
          this.loadingSubject.next(false);
        },
      });
  }

  updateSaga(saga: ISaga) {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    this.api
      .updateSaga(saga)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updatedSaga) => {
          this.sagaSubject.next(updatedSaga as ISaga);
          this.setLocal(updatedSaga as ISaga);
          this.loadingSubject.next(false);
        },
        error: () => {
          this.errorSubject.next('Failed to update saga');
          this.loadingSubject.next(false);
        },
      });
  }

  completeSaga() {
    const current = this.sagaSubject.value;
    if (!current) return;
    if (current.status === 'completed') return;
    if (current.current_step === SagaStepEnum.completed) return;

    const saga: ISaga = {
      ...current,
      current_step: SagaStepEnum.completed,
      status: 'completed',
      saga_data: {
        ...current.saga_data,
        completed: this.momentUtcNow(),
      },
    };

    this.updateSaga(saga);
    this.isSagaComplete.set(true);
  }

  clearCompleteFlag() {
    // Prevent popup from persisting
    this.isSagaComplete.set(false);
  }

  changeSagaStep(step: SagaStepEnum) {
    const current = this.sagaSubject.value;
    if (!current) return;

    if (current.current_step === SagaStepEnum.completed) return;

    const saga: ISaga = {
      ...current,
      current_step: step,
      saga_data: current.saga_data ?? {},
    };
    this.updateSaga(saga);
  }

  updateSagaData(data: Partial<SagaData>) {
    const current = this.sagaSubject.value;

    if (current) {
      const saga: ISaga = {
        ...current,
        saga_data: { ...current.saga_data, ...data },
      };
      this.updateSaga(saga);
    }
  }

  recordEvent(eventName: keyof SagaData) {
    const current = this.sagaSubject.value;
    if (!current) return;

    const saga: ISaga = {
      ...current,
      saga_data: {
        ...current.saga_data,
        [eventName]: this.momentUtcNow(),
      },
    };

    this.updateSaga(saga);
  }

  recordUseCaseSeen(testId: number) {
    const current = this.sagaSubject.value;
    if (!current) return;

    const saga: ISaga = {
      ...current,
      saga_data: {
        ...(current.saga_data ?? {}),
        useCasesSeen: {
          ...(current.saga_data.useCasesSeen ?? {}),
          [testId]: this.momentUtcNow(),
        },
      },
    };

    this.updateSaga(saga);
  }

  getUseCaseSeen(useCase: IUseCaseItem): boolean {
    const current = this.sagaSubject.value;
    if (!current) return false;

    try {
      return Boolean(current.saga_data?.useCasesSeen?.[useCase.test_id]);
    } catch (e) {
      return false;
    }
  }

  getLocal() {
    const sagaConfig = localStorage.getItem('saga_config');
    if (sagaConfig) {
      this.sagaSubject.next(JSON.parse(sagaConfig) as ISaga);
    }
  }

  setLocal(saga: ISaga) {
    localStorage.setItem('saga_config', JSON.stringify(saga));
  }

  clearLocal() {
    localStorage.removeItem('saga_config');
  }

  private momentUtcNow = () => moment().utc().format(AppConstants.DATE_FORMAT.ISO_DATE_TIME);
}
